import { FC } from 'react';
import { CornerRightSvg, CornerSvg } from 'assets/svg-icon';
import { createAvataar } from 'untils/createAvatar';
import { Avatar } from 'ui/Avatar/Avatar';
import { useDispatch } from 'react-redux';
import { getFileRequest } from 'redux/reducers/download/reducer';
import { endpoint } from 'services/endpoint';
import { Video } from 'ui/Video/Video';
import { Audio } from 'ui/Audio/Audio';
import { File } from 'ui/File/File';
import classNames from 'classnames';
import { wrapLinkWithAnchor } from 'untils/wrapLinkWithAnchor';
import parse from 'html-react-parser';
import { NUMDEF } from 'CONST/cardName';
import { ReactComponent as CheckIcon } from '../../assets/svg-icon/checkIcon.svg';
import { formatUnixToTime } from 'untils/centrifuge';
import { getTypeFile } from 'untils/getTypeFile';
import './Chat.scss';
import { IMessageResult } from 'redux/reducers/support/types';

interface IProps {
  // id поточного повідомлення (потрібно для якорів)
  messageId?: string | number;

  chat?: string;
  chats?: Array<IMessageResult>;
  admin: boolean;
  hash: string | undefined;
  hashImage: string | undefined;
  username: string | undefined;

  // forwarded
  fwd_message: string | undefined;
  fwd_name: string | undefined;
  fwd_date: string | undefined;
  fwd_hash_img: string | undefined;
  fwd_type: string | undefined;
  fwd_mime_type?: string | undefined;
  fwd_file_name?: string | undefined;

  // reply
  reply_to_msg_id?: string | undefined;

  // main media
  type: string | undefined;
  mime_type?: string | undefined;
  file_name?: string | undefined;
  time?: string | number | undefined;
}

export const Chat: FC<IProps> = ({
  messageId, // ✅ нове
  chat = '',
  admin,
  hash,
  username,
  hashImage,
  type,
  mime_type,
  file_name,
  time = '',
  fwd_message,
  fwd_name,
  fwd_date,
  fwd_hash_img,
  fwd_type,
  fwd_mime_type,
  fwd_file_name,

  // reply
  reply_to_msg_id = '',
  chats = [],
}) => {
  const dispatch = useDispatch();

  // ---------- HELPERS ----------
  const byType = () => {
    if (mime_type?.includes('document') || type?.includes('document')) return '/storage/document';
    if (mime_type?.includes('image') || type?.includes('image')) return '/storage/photo';
    if (mime_type?.includes('video') || type?.includes('video')) return '/storage/document';
  };

  const onDownload = () => {
    if (type === 'video')
      dispatch(getFileRequest({ endpoint: endpoint.utils.video(hashImage || '') }));
    if (type === 'document')
      dispatch(getFileRequest({ endpoint: endpoint.utils.document(hashImage || '') }));
    if (type === 'photo' || type === 'image')
      dispatch(getFileRequest({ endpoint: endpoint.utils.photo(hashImage || '') }));
  };

  const onTypeFile = () => {
    // 🎥 Відео
    if (mime_type?.includes('video') || type?.includes('video')) {
      return (
        <div className="wrap-video">
          <div className="video">
            <div className="video__container">
              {hashImage?.startsWith('blob:') || hashImage?.startsWith('data:') ? (
                <video src={hashImage} controls style={{ maxWidth: '100%', borderRadius: 8 }} />
              ) : (
                <Video type={mime_type} endpoint={byType()} hash={String(hashImage)} />
              )}
            </div>
          </div>
        </div>
      );
    }

    // 🖼 Зображення
    if (
      mime_type?.includes('image') ||
      mime_type?.includes('photo') ||
      type?.includes('photo') ||
      type?.includes('image')
    ) {
      return (
        <div className="photo-wrap pointer" onClick={onDownload}>
          {hashImage?.startsWith('blob:') || hashImage?.startsWith('data:') ? (
            <img src={hashImage} alt={file_name || ''} />
          ) : (
            <Avatar type={type} endpoint={byType()} hash={String(hashImage)} />
          )}
        </div>
      );
    }

    // 🎵 Аудіо
    if (mime_type?.includes('audio') || type?.includes('audio')) {
      return (
        <div className="wrap-video">
          <div className="video">
            {hashImage?.startsWith('blob:') || hashImage?.startsWith('data:') ? (
              <audio src={hashImage} controls style={{ width: '100%' }} />
            ) : (
              <Audio type={mime_type} endpoint={byType()} hash={String(hashImage)} />
            )}
          </div>
        </div>
      );
    }

    // 📄 Інші типи файлів
    return (
      <div className="wrap-video">
        <div className={classNames('video', { 'text-color-admin': admin })}>
          {hashImage?.startsWith('blob:') || hashImage?.startsWith('data:') ? (
            <a
              href={hashImage}
              download={file_name}
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              {getTypeFile(file_name?.slice(file_name?.lastIndexOf('.') + 1) || '')}
              <span>{file_name}</span>
            </a>
          ) : (
            <File
              type={mime_type}
              endpoint={byType()}
              hash={String(hashImage)}
              file_name={file_name}
            />
          )}
        </div>
      </div>
    );
  };

  // ---------- FORWARDED ----------
  const byTypeForward = () => {
    if (fwd_mime_type?.includes('document') || fwd_type?.includes('document'))
      return '/storage/document';
    if (
      fwd_mime_type?.includes('image') ||
      fwd_type?.includes('image') ||
      fwd_type?.includes('photo')
    )
      return '/storage/photo';
    if (fwd_mime_type?.includes('video') || fwd_type?.includes('video')) return '/storage/document';
    if (fwd_mime_type?.includes('audio') || fwd_type?.includes('audio')) return '/storage/document';
    return '/storage/document';
  };

  const onDownloadForward = () => {
    if (!fwd_hash_img) return;
    if (fwd_type?.includes('video'))
      dispatch(getFileRequest({ endpoint: endpoint.utils.video(fwd_hash_img) }));
    else if (fwd_type?.includes('document'))
      dispatch(getFileRequest({ endpoint: endpoint.utils.document(fwd_hash_img) }));
    else if (fwd_type?.includes('photo') || fwd_type?.includes('image'))
      dispatch(getFileRequest({ endpoint: endpoint.utils.photo(fwd_hash_img) }));
  };

  const onTypeFileForward = () => {
    if (!fwd_hash_img && !fwd_type && !fwd_mime_type) return null;

    // 🎥 Відео (forward)
    if (fwd_mime_type?.includes('video') || fwd_type?.includes('video')) {
      return (
        <div className="wrap-video">
          <div className="video">
            <div className="video__container">
              {fwd_hash_img?.startsWith('blob:') || fwd_hash_img?.startsWith('data:') ? (
                <video src={fwd_hash_img} controls style={{ maxWidth: '100%', borderRadius: 8 }} />
              ) : (
                <Video
                  type={fwd_mime_type}
                  endpoint={byTypeForward()}
                  hash={String(fwd_hash_img)}
                />
              )}
            </div>
          </div>
        </div>
      );
    }

    // 🖼 Зображення (forward)
    if (
      fwd_mime_type?.includes('image') ||
      fwd_mime_type?.includes('photo') ||
      fwd_type?.includes('photo') ||
      fwd_type?.includes('image')
    ) {
      return (
        <div className="photo-wrap pointer" onClick={onDownloadForward}>
          {fwd_hash_img?.startsWith('blob:') || fwd_hash_img?.startsWith('data:') ? (
            <img src={fwd_hash_img} alt={fwd_file_name || ''} />
          ) : (
            <Avatar type={fwd_type} endpoint={byTypeForward()} hash={String(fwd_hash_img)} />
          )}
        </div>
      );
    }

    // 🎵 Аудіо (forward)
    if (fwd_mime_type?.includes('audio') || fwd_type?.includes('audio')) {
      return (
        <div className="wrap-video">
          <div className="video">
            {fwd_hash_img?.startsWith('blob:') || fwd_hash_img?.startsWith('data:') ? (
              <audio src={fwd_hash_img} controls style={{ width: '100%' }} />
            ) : (
              <Audio type={fwd_mime_type} endpoint={byTypeForward()} hash={String(fwd_hash_img)} />
            )}
          </div>
        </div>
      );
    }

    // 📄 Інші типи файлів (forward)
    return (
      <div className="wrap-video">
        <div className={classNames('video', { 'text-color-admin': admin })}>
          {fwd_hash_img?.startsWith('blob:') || fwd_hash_img?.startsWith('data:') ? (
            <a
              href={fwd_hash_img}
              download={fwd_file_name}
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              {getTypeFile(fwd_file_name?.slice(fwd_file_name?.lastIndexOf('.') + 1) || '')}
              <span>{fwd_file_name}</span>
            </a>
          ) : (
            <File
              type={fwd_mime_type}
              endpoint={byTypeForward()}
              hash={String(fwd_hash_img)}
              file_name={fwd_file_name}
            />
          )}
        </div>
      </div>
    );
  };

  // ---------- REPLY (Пошук + клік-скрол) ----------
  const replyMsg = reply_to_msg_id
    ? chats.find(
        (m: any) => String((m as any).messageId ?? (m as any).id ?? '') === String(reply_to_msg_id),
      )
    : undefined;

  const scrollToReply = () => {
    if (!reply_to_msg_id) return;
    const target = document.getElementById(`msg-${reply_to_msg_id}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // підсвітити на 1.2s
      target.classList.add('chat-list--highlight');
      window.setTimeout(() => target.classList.remove('chat-list--highlight'), 1200);
    }
  };

  const renderReplyPreview = () => {
    if (!replyMsg) return null;

    const rUser = (replyMsg as any)?.username ?? (replyMsg as any)?.user_name ?? '';
    const rText = (replyMsg as any)?.message ?? (replyMsg as any)?.text ?? '';
    const rTime =
      (replyMsg as any)?.time ?? (replyMsg as any)?.create_at ?? (replyMsg as any)?.created_at;
    const rType = (replyMsg as any)?.type ?? '';
    const rMime = (replyMsg as any)?.mime_type ?? '';
    const rFileName = (replyMsg as any)?.file_name ?? '';
    const rHashImg =
      (replyMsg as any)?.hashImage ?? (replyMsg as any)?.hash_img ?? (replyMsg as any)?.hash ?? '';

    const isVideo =
      (rMime && rMime.includes('video')) || (rType && String(rType).includes('video'));
    const isImage =
      (rMime && (rMime.includes('image') || rMime.includes('photo'))) ||
      (rType && (String(rType).includes('image') || String(rType).includes('photo')));
    const isAudio =
      (rMime && rMime.includes('audio')) || (rType && String(rType).includes('audio'));
    const isDocument =
      (rMime && rMime.includes('document')) ||
      (rType && String(rType).includes('document')) ||
      (!isVideo && !isImage && !isAudio && (rFileName || rHashImg));

    const mediaBadge = isVideo
      ? '🎥 Video'
      : isImage
      ? '🖼 Image'
      : isAudio
      ? '🎵 Audio'
      : isDocument
      ? '📄 File'
      : '';

    return (
      <button type="button" className="chat-list__reply" onClick={scrollToReply}>
        <div className="chat-list__reply-head">
          <span className="chat-list__reply-label">Reply to</span>
          {rUser && <span className="chat-list__reply-from">{rUser}</span>}
          {rTime && (
            <span className="chat-list__reply-time">{formatUnixToTime(Number(rTime))}</span>
          )}
        </div>
        {mediaBadge && <div className="chat-list__reply-media">{mediaBadge}</div>}
        {rText && (
          <div className="chat-list__reply-text">{parse(wrapLinkWithAnchor(String(rText)))}</div>
        )}
      </button>
    );
  };

  // ---------- RENDER ----------
  const MessageBody = () => (
    <>
      {/* --- Reply block (клікабельний) --- */}
      {replyMsg && renderReplyPreview()}

      {/* --- Forwarded block --- */}
      {(fwd_message || fwd_name || fwd_date || fwd_hash_img || fwd_type) && (
        <div className="chat-list__forward">
          <div className="chat-list__forward-head">
            <span className="chat-list__forward-label">Forwarded From</span>
            {fwd_name && <span className="chat-list__forward-from">{fwd_name}</span>}
            {fwd_date && (
              <span className="chat-list__forward-time">{formatUnixToTime(Number(fwd_date))}</span>
            )}
          </div>
          {fwd_hash_img !== 'undefined' && onTypeFileForward()}
          {fwd_message && (
            <div className="chat-list__forward-text">{parse(wrapLinkWithAnchor(fwd_message))}</div>
          )}
        </div>
      )}

      {/* Основний контент повідомлення */}
      <div className="chat-list__text">
        {hashImage || type ? onTypeFile() : parse(wrapLinkWithAnchor(chat))}
      </div>

      <div className={admin ? 'chat-list__time-admin' : 'chat-list__time-user'}>
        <span>{formatUnixToTime(time)}</span>
        <CheckIcon />
      </div>
    </>
  );

  // id якоря для цього повідомлення
  const rootId = messageId != null ? `msg-${messageId}` : undefined;

  return admin ? (
    <div className="chat-list chat-list--right" id={rootId}>
      <div
        className={classNames('chat-list__text-wrap chat-list__text-wrap--right', {
          'text-color-admin': admin,
        })}
      >
        <div className="chat-list__corner chat-list__corner--right">
          <CornerRightSvg />
        </div>
        <MessageBody />
      </div>
    </div>
  ) : (
    <div className="chat-list" id={rootId}>
      {hash && NUMDEF !== hash ? (
        <div className="chat-list-avatar">
          <Avatar hash={String(hash)} />
        </div>
      ) : (
        <div className="avatar-custom chat-list__img">
          <span>{createAvataar(username || '')}</span>
        </div>
      )}

      <div className={classNames('chat-list__text-wrap', { 'text-color-admin': admin })}>
        <div className="chat-list__name-user">{username}</div>
        <div className="chat-list__corner">
          <CornerSvg />
        </div>
        <MessageBody />
      </div>
    </div>
  );
};
